// Package imports:
import React, { useEffect, useMemo, useState, useRef } from "react";
// Component imports:
import Button from "../../../ui-elements/Button/Button";
import Modal from "../../../ui-elements/Modal/Modal";
import ProgressBar from "../../../ui-elements/ProgressBar/ProgressBar";
// Service imports:
import { GET_KELDAN_API_URL } from "../../../services/config";
// Type imports:
import { IKycIndividuals, KnowYourIndividual, KnowYourIndividualError } from "../../../types/DueDiligenceTypes";
import Table from "../../../ui-elements/Table/Table";
import { Fetched } from "../../../types/Types";
import { getCountryName, getTableDate, insertDash } from "../../../services/utils";
import Alert from "../../../ui-elements/Alert/Alert";

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const isWithin30Days = (dateStr?: string | null): boolean => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const now = new Date();
    return now.getTime() - date.getTime() < THIRTY_DAYS_MS;
};

interface IProps {
    show: boolean;
    close: () => void;
    selectedIndividuals: KnowYourIndividual[];
    individualKYCPrice: number;
    setIndividuals: React.Dispatch<React.SetStateAction<Fetched<KnowYourIndividual[]>>>;
    clearChecked: () => void;
}

const BATCH_SIZE = 20;

const GetKYCOverviewIndividualsModal: React.FC<IProps> = ({
    show,
    close,
    selectedIndividuals,
    individualKYCPrice,
    setIndividuals,
    clearChecked
}) => {
    const [MODAL_IS_CLOSEABLE, setMODAL_IS_CLOSABLE] = useState(true);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [completedCount, setCompletedCount] = useState(0);
    const [completed, setCompleted] = useState(false);
    const [errors, setErrors] = useState<KnowYourIndividualError[]>([]);
    // Fixed total captured once per open/submit
    const progressTotalRef = useRef<number>(0);

    const handleSubmit = async () => {
        setMODAL_IS_CLOSABLE(false);
        // Capture fixed total at start of submission
        progressTotalRef.current = selectedIndividuals.length;
        setHasSubmitted(true);
        for (let i = 0; i < selectedIndividuals.length; i += BATCH_SIZE) {
            const batch = selectedIndividuals.slice(i, i + BATCH_SIZE);
            await submitBatch(batch);
            await new Promise(res => setTimeout(res, 500));
        }
    };

    const submitBatch = async (batch: KnowYourIndividual[]) => {
        const url = `${GET_KELDAN_API_URL()}/DueDiligence/StartIndividualKyc`;
        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(batch.map(ind => ind.individualCustomerId))
            });
            if (!response.ok) {
                throw new Error("Failed to submit batch");
            }
            const data: IKycIndividuals = await response.json();
            // Update individuals with the new status from successfulRiskScores
            setIndividuals(prev =>
                !prev || !Array.isArray(prev)
                    ? prev
                    : prev.map(i =>
                        data.successfulRiskScores.find(u => u.individualCustomerId === i.individualCustomerId) || i
                    )
            );
            setErrors(prev => [...prev, ...data.errorRiskScores]);
            setCompletedCount(prev => prev + batch.length);
        } catch (error) {
            console.error("Error submitting batch:", error);
        }
    }

    // completed only updates after a 500ms delay using fixed total
    useEffect(() => {
        const handler = setTimeout(() => {
            setCompleted(completedCount === progressTotalRef.current);
            setMODAL_IS_CLOSABLE(completedCount === progressTotalRef.current);
        }, 500);
        return () => clearTimeout(handler);
    }, [completedCount]);

    const errorCount = useMemo(() => {
        return errors.length;
    }, [errors]);

    useEffect(() => {
        if (show) {
            // Capture initial total once when opening (before submit)
            progressTotalRef.current = selectedIndividuals.length;
            setCompletedCount(0);
            setCompleted(false);
            setHasSubmitted(false);
            setErrors([]);
        } else if (!show && hasSubmitted) {
            // After closing following submission, clear checked outside
            clearChecked();
        }
    }, [show]);

    return (
        <Modal
            show={show}
            onHide={MODAL_IS_CLOSEABLE ? close : () => { }}
            dontCloseOnClickOutside
            header={"Hefja áreiðanleikakönnun"}
            hasCloseButton={MODAL_IS_CLOSEABLE}
            body={
                !hasSubmitted
                    ? <div className="get-kyc-overview">
                        <div className="info-area">
                            <p className="paragraph--bold">Þú ert að fara að framkvæma áreiðanleikakönnun á {selectedIndividuals.length} {(selectedIndividuals.length % 10 === 1 && selectedIndividuals.length % 100 !== 11) ? 'einstakling.' : 'einstaklingum.'}</p>
                            <Table
                                tableSize="sm"
                                scrollable
                                maxBodyHeight="270px"
                                data={selectedIndividuals}
                                columns={[{
                                    title: 'Einstaklingur',
                                    renderCell: ({ name }) => name,
                                    textAlign: 'left'
                                }, {
                                    title: 'Kennitala',
                                    renderCell: ({ ssn, country }) => ssn ? insertDash(ssn) : country ? getCountryName(country) : '',
                                    textAlign: 'left'
                                }, {
                                    title: 'Könnun síðast hafin',
                                    textAlign: 'left',
                                    renderCell: ({ riskscoreCreated }) => {
                                        return riskscoreCreated ? getTableDate(riskscoreCreated, "DD/MM/YYYY", ".") : '';
                                    },
                                    cellBackgroundColor: ({ riskscoreCreated }) => isWithin30Days(riskscoreCreated) ? '#FEFBDC' : undefined
                                }]} />
                            <p className="paragraph"><span className="paragraph--bold">Athugið hver framkvæmd áreiðanleikakönnun kostar {individualKYCPrice.toLocaleString('de-DE')} kr. án vsk.</span> Innifalið eru helstu uppflettingar á móti válistum (meðal annars íslenskur PEP listi, alþjóðlegir válistar og áhættusöm ríkisföng), rafræn innskráning, spurningalisti sendur út og móttekinn, skráð áhættumat, utanumhald og gagnageymsla.</p>
                            {selectedIndividuals.some(i => isWithin30Days(i.riskscoreCreated)) &&
                                <Alert
                                    type="alert"
                                    headText="Athugið viðskiptavinir á þessum lista eru þegar með hafna könnun á síðustu 30 dögum."
                                />
                            }
                            <p className="paragraph--bold">Viltu halda áfram?</p>
                        </div>

                        <div style={{ marginTop: 24 }}>
                            <Button buttonType="secondary" onClick={close}>Til baka</Button>
                            <Button style={{ marginLeft: 20 }} onClick={handleSubmit}>Staðfesta</Button>
                        </div>
                    </div>
                    : !completed
                        ? <div className="get-kyc-overview">
                            <ProgressBar
                                current={completedCount}
                                total={progressTotalRef.current}
                                showPercentage={true}
                                size="large"
                            />
                            <p className="paragraph">Áreiðanleikakönnun hefur verið send á {completedCount - errorCount} einstaklinga.</p>
                            <p className="paragraph">Ekki tókst að senda áreiðanleikakönnun á {errorCount} einstaklinga.</p>
                        </div>
                        : <div className="get-kyc-overview">
                            <p className="paragraph">Áreiðanleikakönnun er hafin hjá {completedCount - errorCount} einstaklingum.</p>
                            {errorCount > 0 && (
                                <>
                                    <p className="paragraph">Ekki tókst að hefja áreiðanleikakönnun hjá {errorCount} einstaklingum.</p>

                                    <Table
                                        tableSize="sm"
                                        scrollable
                                        maxBodyHeight="270px"
                                        data={errors}
                                        columns={[{
                                            title: "Nafn",
                                            renderCell: ({ name }) => name,
                                            textAlign: "left"
                                        }, {
                                            title: "Kennitala",
                                            renderCell: ({ ssn, country }) => ssn ? insertDash(ssn) : country ? getCountryName(country) : '',
                                            textAlign: "left"
                                        }, {
                                            title: "Ástæða",
                                            renderCell: ({ error }) => error,
                                            textAlign: "left"
                                        }]}
                                    />
                                </>
                            )}
                            <Button style={{ marginTop: '20px' }} onClick={close}>Loka</Button>
                        </div>
            }
        />
    );
};

export default GetKYCOverviewIndividualsModal;
